import { useState } from 'react'
import { Button, Checkbox, DatePicker, Form, Modal, Select, Space, Tooltip, message } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { useAvailableFlags, useRunFlags } from '../../../api/flagAdmin'
import FlagRunResults from './FlagRunResults'

const { RangePicker } = DatePicker

const ExistingFlagsForm = () => {
  const { data: flags, isLoading: flagsLoading } = useAvailableFlags()
  const runFlagsMutation = useRunFlags()
  const [selectedFlags, setSelectedFlags] = useState<string[]>([])
  const [avvikelsetyp, setAvvikelsetyp] = useState<string[]>([])
  const [period, setPeriod] = useState<[string, string] | null>(null)
  const [activeRunIds, setActiveRunIds] = useState<number[]>([])
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)

  const canSubmit = selectedFlags.length > 0 && period !== null

  const handleRun = async (dryRun: boolean) => {
    if (!period) return
    try {
      const result = await runFlagsMutation.mutateAsync({
        flagNames: selectedFlags,
        periodFrom: period[0],
        periodTo: period[1],
        avvikelsetyp: avvikelsetyp[0]?.trim() || '',
        dryRun,
      })
      setActiveRunIds(result.runIds)
      message.success(dryRun ? 'Dry-run startad' : 'Körning startad')
    } catch {
      message.error('Kunde inte starta körning')
    }
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <Form layout="vertical">
        <Form.Item label="Välj flaggor" required>
          {flagsLoading ? (
            <p>Laddar flaggor...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Checkbox
                indeterminate={selectedFlags.length > 0 && selectedFlags.length < (flags?.length ?? 0)}
                checked={flags !== undefined && flags.length > 0 && selectedFlags.length === flags.length}
                style={{ marginLeft: 0, fontWeight: 500, paddingBottom: 8, marginBottom: 4, borderBottom: '1px solid #f0f0f0' }}
                onChange={(e) => setSelectedFlags(e.target.checked ? flags?.map((f) => f.name) ?? [] : [])}
              >
                Välj alla
              </Checkbox>
              {flags?.map((flag) => (
                <Checkbox
                  key={flag.name}
                  checked={selectedFlags.includes(flag.name)}
                  style={{ marginLeft: 0 }}
                  onChange={(e) =>
                    setSelectedFlags(e.target.checked ? [...selectedFlags, flag.name] : selectedFlags.filter((f) => f !== flag.name))
                  }
                >
                  {flag.displayName}
                </Checkbox>
              ))}
            </div>
          )}
        </Form.Item>

        <Form.Item label="Period (månad)" required>
          <RangePicker picker="month" onChange={(_, dateStrings) => setPeriod(dateStrings[0] && dateStrings[1] ? [dateStrings[0], dateStrings[1]] : null)} />
        </Form.Item>

        <Form.Item label={<span>Avvikelsetyp <Tooltip title="Klassificerar körningen, t.ex. Välfärdsbrott eller Systemfel. Sparas på varje skapad flagga i databasen. Valfritt — kan lämnas tomt."><InfoCircleOutlined style={{ color: '#999' }} /></Tooltip></span>}>
          <Select
            placeholder="Välj eller skriv in avvikelsetyp"
            value={avvikelsetyp}
            onChange={(values: string[]) => setAvvikelsetyp(values.length > 1 ? [values[values.length - 1]] : values)}
            allowClear
            mode="tags"
            options={[
              { value: 'Välfärdsbrott', label: 'Välfärdsbrott' },
              { value: 'Systemfel/kvalitet', label: 'Systemfel/kvalitet' },
              { value: 'Proaktiv individuppföljning', label: 'Proaktiv individuppföljning' },
            ]}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" disabled={!canSubmit} loading={runFlagsMutation.isLoading} onClick={() => handleRun(true)}>
              Dry-run
            </Button>
            <Button
              type="primary"
              danger
              disabled={!canSubmit}
              loading={runFlagsMutation.isLoading}
              onClick={() => setConfirmModalOpen(true)}
            >
              Spara till DB
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        title="Bekräfta sparning"
        open={confirmModalOpen}
        onOk={() => {
          setConfirmModalOpen(false)
          handleRun(false)
        }}
        onCancel={() => setConfirmModalOpen(false)}
        okText="Spara"
        cancelText="Avbryt"
        okButtonProps={{ danger: true }}
      >
        <p>
          Är du säker på att du vill spara flaggresultaten till databasen?
        </p>
        <p>
          <strong>Flaggor:</strong> {selectedFlags.join(', ')}
          <br />
          <strong>Period:</strong> {period?.[0]} — {period?.[1]}
          <br />
          <strong>Avvikelsetyp:</strong> {avvikelsetyp[0] || '(ingen)'}
        </p>
      </Modal>

      {activeRunIds.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Resultat</h3>
          {activeRunIds.map((runId) => (
            <FlagRunResults key={runId} runId={runId} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ExistingFlagsForm
